import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { liveCollection } from '../../data/collectes';
import { useLiveTruck, useTruckViewer } from '../../hooks/useLiveTruck';
import { TruckBoostButton } from './TruckBoostButton';
import { token } from '../../utils/theme';
const TRUCK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';
const HOME_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 10v10h14V10"/></svg>';
const truckIcon = L.divIcon({
  className: 'collecte-truck',
  html: `<span class="grid h-8 w-8 place-items-center rounded-xl border-2 border-white bg-ville-blue shadow-lg shadow-ville-navy/30">${TRUCK_SVG}</span>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});
const homeIcon = L.divIcon({
  className: 'collecte-home',
  html: `<span class="grid h-7 w-7 place-items-center rounded-full border-[3px] border-white bg-ville-navy shadow-md shadow-ville-navy/30">${HOME_SVG}</span>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

/**
 * Aperçu du camion dans la conversation ATNA : même trajet routier et même
 * progression en direct que la carte du module Collectes.
 */
export function TruckPreviewMap() {
  const {
    home,
    truck
  } = liveCollection;
  useTruckViewer();
  const {
    route,
    done,
    remaining,
    truckPosition
  } = useLiveTruck();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: home,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Le dialogue s'ouvre en animation : on recalcule la taille une fois posé.
    const timer = window.setTimeout(() => map.invalidateSize(), 320);
    return () => {
      window.clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [home]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || route.length < 2) return;
    map.fitBounds(L.latLngBounds(route), {
      padding: [24, 24]
    });
  }, [route]);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    L.polyline(done, {
      color: '#94a3b8',
      weight: 4,
      opacity: 0.65
    }).addTo(layer);
    L.polyline(remaining, {
      color: token('--ville-blue', '#2f5bc9'),
      weight: 5,
      dashArray: '1 9',
      lineCap: 'round'
    }).addTo(layer);
    L.marker(home, {
      icon: homeIcon,
      title: 'Votre adresse',
      alt: 'Votre adresse'
    }).addTo(layer);
    L.marker(truckPosition, {
      icon: truckIcon,
      title: truck,
      alt: `Position du ${truck}`
    }).addTo(layer);
  }, [done, remaining, truckPosition, home, truck]);
  return <div className="relative isolate z-0" data-id="element-1059" data-mp="174ejbycq0e1u">
      <div ref={containerRef} role="application" aria-label={`Trajet du ${truck} vers votre adresse`} className="h-[200px] w-full bg-ville-mist" data-id="element-1060" data-mp="175fx4al4fen9" />
      <TruckBoostButton className="absolute bottom-2.5 left-2.5 z-[500]" data-id="element-1061" data-mp="1h3dhuo3rutb4" />
    </div>;
}